import { ReactNode } from 'react';

import type { Ryecon } from '@dbt-labs/sourdough';

import { DagContextMenuOption } from '../components/DagContextMenu';
import { Prettify } from '../utils/type';
import { DagreResult } from './DagreTypes';
import { InteractionDagNode } from './internalDag';
import type { Grain, Lens } from './InternalDagInterfaces';
import {
  DagNodeInternalBase,
  DbtDagNodeInternal,
  InternalDagContextType,
} from './InternalDagInterfaces';

/**
 * - `pending` - The DAG is currently loading.
 * - `error` - The DAG failed to load.
 * - `success` - The DAG loaded successfully.
 * (Loading is deprecated)
 */
export type QueryStatus =
  | 'pending'
  /** @deprecated use `pending` instead */
  | 'loading'
  | 'error'
  | 'success';

export const LOADING_STATES: QueryStatus[] = ['pending', 'loading'];

/**
 * DbtDagNode uses resourceType to provide colors and iconography.
 */
export type DbtDagNode = DbtDagNodeInternal &
  DagNodeInternalBase & { data?: Record<string, any> };

/**
 * A collection of stats about the DAG layout and rendering.
 */
export interface DagStats {
  /**
   * If provided to the DAG, this ID can help identify which request
   * caused this DAG event.
   */
  requestId: string;
  /**
   * Number of nodes involved in this event.
   */
  nodeCount: number;
  /**
   * Number of edges involved in this event.
   */
  edgeCount: number;
  /**
   * Time in milliseconds layout step took
   */
  layoutDurationMillis: number | null;
  /**
   * Timestamp for when the render step started
   */
  renderStartTimestamp: number;
  /**
   * Time in milliseconds the render step took
   */
  renderDurationMillis: number;
  /**
   * True when the DAG is made to render despite high complexity
   */
  forceRenderComplexDag: boolean;
}

export type ZoomMode = 'fitAll' | 'primary' | '1+primary+1';

export interface DagProps<TDbtDagNode extends DbtDagNode>
  extends InternalDagContextType {
  /**
   * The nodes which make up the DAG.
   */
  nodes: TDbtDagNode[];
  /**
   * The active dbtCloudProject, should only be set if {@link Grain} is `project`
   *
   * Required for a full mesh implementation, as well as the following features:
   * @see {@link InternalDagContextType.useExternalConnections | useExternalConnections}
   * @see {@link DagNodeInternalBase.dbtCloudProject | nodes.dbtCloudProject}
   */
  activeDbtCloudProject: string | null;
  /**
   * @see {@link Grain} for more details.
   */
  grain: Grain;
  /**
   * The ID of the nodes which are primary. The primary nodes
   * are the nodes which is most relevant in this current context.
   *
   * This can often be an array of one ID. primaryNodeIds is used
   * to determine reset states, highlighting logic, and more.
   *
   * Nodes can also be selected by clicking on them, but this does not
   * change the primary node, only highlighting.
   */
  primaryNodeIds?: string[];
  /**
   * Returns a list of ContextMenu options. If not defined, there will
   * be no ContextMenu (menu that opens on right-click).
   */
  getContextMenuOptions?: (
    node: InteractionDagNode<TDbtDagNode>,
  ) => DagContextMenuOption[];
  /**
   * An event handler for when a user interacts with a node.
   */
  onNodeInteraction?: (event: OnNodeInteractionEvent<TDbtDagNode>) => void;
  /**
   * Used to determine if a DAG should be displayed or another state
   * ("loading" | "error" | "success").
   * @see {@link QueryStatus}
   */
  status?: QueryStatus;
  /**
   * If true, displays a loading animation.
   * If false, ignores `status` of 'loading'.
   */
  showLoadingScreen?: boolean;
  /**
   * Messages that will be displayed to the user when the
   * {@link DagProps.status | status} is 'error'.
   */
  errorMessages?: string[];
  /**
   * Called when the DAG starts computing the layout (laying out DAGs
   * is computionally expensive in dagre!)
   */
  onDagLayoutStart?: (
    stats: Omit<
      DagStats,
      'layoutDurationMillis' | 'renderStartTimestamp' | 'renderDurationMillis'
    >,
  ) => void;
  /**
   * Called when the DAG layout result is ready and before rendering
   * returning a result will override the default result
   */
  onDagLayoutResult?: (
    result: DagreResult<TDbtDagNode>,
  ) => DagreResult<TDbtDagNode> | void;
  /**
   * Called when the DAG starts rendering (usually fast even with large dags)
   */
  onDagRenderStart?: (stats: Omit<DagStats, 'renderDurationMillis'>) => void;
  /**
   * Called when the DAG successfully completes rendering
   */
  onRenderComplete?: (stats: DagStats) => void;
  /**
   * Called when the DAG warns user about complexity instead of displaying DAG
   */
  onComplexityWarn?: () => void;
  /**
   * Called when the DAG container is resized, debounced
   */
  onResize?: () => void;
  /**
   * Offsets on the edge of the DAG view to help define the center of the view.
   * We will not render default DAG inside the padded areas, but a user can zoom,
   * scroll, pan and the DAG can occupy the padded areas.
   */
  yOffset?: number;
  /**
   * Offsets on the edge of the DAG view to help define the center of the view.
   * We will not render default DAG inside the padded areas, but a user can zoom,
   * scroll, pan and the DAG can occupy the padded areas.
   */
  xOffset?: number;
  /**
   * A 0 to 1 scale on how much padding to provide when zooming the DAG.
   * Defaults to 0.05
   */
  padding?: number;
  /**
   * 1.0 is normal zoom.
   */
  minZoom?: number;
  /**
   * Defaults to `fitAll`
   */
  zoomMode?: ZoomMode;
  /**
   * If true, the DAG will zoom in and out when scrolling. Defaults to true.
   */
  scrollToZoom?: boolean;
  /**
   * Will display a minimap if enabled.
   */
  enableMinimap?: boolean;
  /**
   * If disabled, the DAG will retain its zoom state but recenter when the
   * window is resized. Defaults true.
   */
  resetZoomOnResize?: boolean;
  /**
   * Defaults to true. If false, the DAG will not render the reset button
   * in the toolbar. You should likely only disable this if you plan to
   * replace it with another button that does the same thing.
   */
  showResetButton?: boolean;
  /**
   * Optionally provided testId for the DAG
   */
  testId?: string;
  /**
   * The current lens being rendered
   */
  lens?: Lens | null;
  /**
   * If true, the magnified node will not be rendered.
   */
  disableMagnifiedNode?: boolean;

  children?: ReactNode;
}

export type OnNodeInteractionEvent<TDbtDagNode extends DbtDagNode> =
  | {
      /**
       * The type of interaction which occurred on the {@link targetNode}
       */
      interactionType: 'single_click' | 'double_click' | 'right_click';
      /**
       * The node which was interacted upon
       */
      targetNode: InteractionDagNode<TDbtDagNode>;
    }
  | {
      /**
       * The type of interaction which occurred on the {@link targetNode}
       */
      interactionType: 'clear_selection';
      /**
       * The node which was interacted upon
       */
      targetNode: null;
    };

/**
 * All of the different kinds of Node Interactions.
 * @see {@link OnNodeInteractionEvent}
 */
export type NodeInteractionType = OnNodeInteractionEvent<any>['interactionType'];

/**
 * Allows you to specify a custom toolbar item.
 */
export type ToolbarItem = Prettify<{
  /**
   * Optional label of the button, displayed next to the icon.
   */
  label?: string;
  /**
   * The tooltip of the button, displayed on hover.
   */
  tooltip: string;
  /**
   * Called when the button is pressed.
   */
  action?: () => void;
  testId?: string;
  isDisabled?: boolean;
  className?: string;
  ryecon: Ryecon;
}>;
